// ═══ 말소증 카드 — [보기] 카드 아래 인라인 미리보기 · [공유] 다회용 링크 복사 (B3 리뷰 P10) ═══
// 기존 components/ 말소증 뷰어는 한글 고정 + 카카오 2단계라 재사용하지 않는다 — blob→objectURL·revoke 방식만 같다.
// [보기]: api.fetchMalsoCert(인증 필요 → Response.blob) → img/iframe. 새 탭은 async 뒤라 Safari 팝업 차단 → 인라인.
//         objectURL 은 닫을 때·언마운트 때 회수한다(effect cleanup 한 곳).
// [공유]: api.malsoShare(다회용 링크) → clipboard. 1회용 다운로드 링크는 쓰지 않는다. 카카오 호출 없음.
//         Safari 는 비동기 뒤 clipboard 쓰기를 막는다(NotAllowedError) → 받은 링크를 읽기전용 칸에 남겨 직접 복사.
// props: { id, showToast }
import React, { useState, useEffect } from 'react';
import { T, R, FONT } from '../../theme';
import { useT } from '../../../i18n/I18nProvider';
import { fetchMalsoCert, malsoShare } from '../api';
import { describeSimpleError } from '../../simple/errors';
import ErrorText from '../../simple/parts/ErrorText';

var card = { background: T.card, border: '1px solid ' + T.bd, borderRadius: R.card, padding: '12px 16px' };
var head = { display: 'flex', alignItems: 'center', gap: 8 };
var title = { flex: 1, minWidth: 0, fontSize: FONT.cardTitle, fontWeight: 800, color: T.ink };
var btn = { height: 32, padding: '0 14px', borderRadius: R.md, border: '1px solid ' + T.bd, background: T.card,
  color: T.navy, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flex: 'none' };
var preview = { marginTop: 10, borderRadius: R.md, overflow: 'hidden', border: '1px solid ' + T.bd, background: T.head };
var muted = { padding: '20px 0', textAlign: 'center', fontSize: FONT.body, color: T.muted };
var linkBox = { marginTop: 10, width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', borderRadius: R.md,
  border: '1px solid ' + T.bd, background: T.head, color: T.ink, fontSize: FONT.body, fontFamily: 'inherit', direction: 'ltr' };

function MalsoCertCard(props) {
  var t = useT();
  var id = props.id;
  var showToast = props.showToast || function () {};
  var stO = useState(false); var open = stO[0], setOpen = stO[1];
  var stD = useState(null); var doc = stD[0], setDoc = stD[1];        // { url, pdf }
  var stE = useState(null); var err = stE[0], setErr = stE[1];        // Error — 문구는 렌더 시 describeSimpleError
  var stS = useState(false); var sharing = stS[0], setSharing = stS[1];
  var stU = useState(''); var shareUrl = stU[0], setShareUrl = stU[1];  // 복사 실패 시 보여 줄 발급 링크

  useEffect(function () {
    if (!open) return undefined;
    var alive = true, url = '', ctype = '';
    fetchMalsoCert(id).then(function (res) {
      if (!res || !res.ok) throw new Error('');     // 404 는 JSON 본문 — blob 으로 읽지 않는다
      ctype = (res.headers && res.headers.get && res.headers.get('Content-Type')) || '';
      return res.blob();
    }).then(function (b) {
      if (!alive) return;
      url = URL.createObjectURL(b);
      setDoc({ url: url, pdf: ctype.indexOf('application/pdf') >= 0 });
    }).catch(function (e) { if (alive) setErr(e); });
    return function () {
      alive = false;
      if (url) { try { URL.revokeObjectURL(url); } catch (e) { /* 이미 해제됨 */ } }
    };
  // setter 는 useState 가 보장하는 안정 참조 — 배열 구조분해라 린터가 못 알아본다.
  }, [open, id, setDoc, setErr]);

  var toggle = function () { setDoc(null); setErr(null); setOpen(!open); };

  var share = function () {
    if (sharing) return;
    setSharing(true);
    setErr(null);
    setShareUrl('');
    malsoShare(id).then(function (body) {
      var url = body && body.url;
      if (!url) throw new Error('');
      var copy = navigator.clipboard && navigator.clipboard.writeText ? navigator.clipboard.writeText(url) : Promise.reject(new Error(''));
      return copy.then(function () {
        setSharing(false);
        showToast(t('rdMalsoCertLinkCopied'));
      }, function () {
        setSharing(false);
        setShareUrl(url);
      });
    }).catch(function (e) {
      setSharing(false);
      setErr(e);
    });
  };

  var body = null;
  if (open && doc) {
    body = doc.pdf
      ? <iframe title={t('invoiceMalsoDocLabel')} src={doc.url} style={{ width: '100%', height: 520, border: 0, display: 'block' }} />
      : <img alt={t('invoiceMalsoDocLabel')} src={doc.url} style={{ width: '100%', display: 'block' }} />;
  } else if (open && !err) {
    body = <div style={muted}>{t('commonLoading')}</div>;
  }

  return (<div data-part="cert-card" style={card}>
    <div style={head}>
      <span style={title}>{t('invoiceMalsoDocLabel')}</span>
      <button type="button" data-action="cert-view" aria-pressed={open} style={btn} onClick={toggle}>{t('malsoModeCertView')}</button>
      <button type="button" data-action="cert-share" style={btn} disabled={sharing} onClick={share}>{t('smartUploadShare')}</button>
    </div>
    {body ? <div data-preview={doc ? (doc.pdf ? 'pdf' : 'image') : 'loading'} style={preview}>{body}</div> : null}
    {shareUrl ? <input type="text" readOnly data-share-url="" aria-label={t('smartUploadShare')} value={shareUrl} style={linkBox}
      onFocus={function (e) { e.target.select(); }} /> : null}
    {err ? <ErrorText>{describeSimpleError(t, err)}</ErrorText> : null}
  </div>);
}

export default MalsoCertCard;
